import * as THREE from 'three';
import { basic, emis, std } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';
import { figure } from './heroes.ts';

/*
 * Prestado (Caras prestadas, docs/SAGAS.md): the small nightmare that wears borrowed faces. What is left when the
 * face comes off: taller than a man and far too thin, hunched, its skin a pale grey like wet paper that clings to
 * the bones; no face at all, only a smooth oval with a dark seam where a mouth would be; arms that hang past its
 * knees and end in long, jointed fingers. Rags of the face it wore still hang off its shoulders and chest, pale
 * strips that flutter. Bones: hips, chest, head, armL/foreL, armR/foreR, legL/shinL, legR/shinR. Faces +Z.
 * Opaque shared materials, the seam self-lit, a glow sprite (no lights).
 */

export const prestado = (o?: BaseOpts): Model => {
  const c = begin(o, 0.5, 4471);
  const { g, rng } = c;
  // Wet paper: a pale cold grey, its shadows a dirtier grey; the rags lighter and see-through at the edges.
  const skin = std('#c9ccd1', { roughness: 0.55, flatShading: true });
  const skinD = std('#8d9198', { roughness: 0.7, flatShading: true });
  const rag = std('#e4e6ea', { roughness: 0.9, side: THREE.DoubleSide, flatShading: true });
  const seam = emis('#1a1d24', '#7d8cff', 0.8);
  const nail = std('#3a3c44', { roughness: 0.4 });

  const hips = joint(g, V(0, 1.02, -0.02), 'hips');
  const chest = joint(hips.abs, V(0, 1.18, -0.02), 'chest');
  const neck = joint(chest.abs, V(0, 1.68, 0.12), 'head');

  // A long narrow ribcage pitched forward, a pinched waist, the hips a thin bowl.
  const rib = M(new THREE.CylinderGeometry(0.16, 0.1, 0.52, 8), skin, chest.abs);
  rib.position.set(0, 1.42, 0.04);
  rib.rotation.x = 0.28;
  rib.scale.set(1, 1, 0.72);
  for (let i = 0; i < 4; i++) {
    const r = M(new THREE.TorusGeometry(0.13 - i * 0.012, 0.012, 4, 10, Math.PI), skinD, chest.abs);
    r.position.set(0, 1.52 - i * 0.09, 0.09 + i * 0.012);
    r.rotation.set(Math.PI / 2 + 0.3, 0, 0);
  }
  M(new THREE.CylinderGeometry(0.07, 0.11, 0.2, 8), skinD, hips.abs).position.set(0, 1.08, -0.02);
  const pelvis = M(new THREE.SphereGeometry(0.12, 8, 6), skin, hips.abs);
  pelvis.position.set(0, 0.98, -0.02);
  pelvis.scale.set(1.1, 0.7, 0.8);
  // Shoulders: knobs too high, the neck long and craned forward.
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.07, 8, 6), skin, chest.abs).position.set(k * 0.17, 1.66, 0.08);
  limb(chest.abs, V(0, 1.62, 0.08), V(0, 1.78, 0.2), 0.045, 0.035, skin, 6);

  // The head: a smooth egg with no face, a dark seam across where the mouth was, two hollows for eyes.
  const head = G(neck.abs, 0, 1.86, 0.25);
  const skull = M(new THREE.SphereGeometry(0.13, 12, 10), skin, head);
  skull.scale.set(0.85, 1.25, 0.95);
  const seamM = M(new THREE.BoxGeometry(0.12, 0.012, 0.02), seam, head);
  seamM.position.set(0, -0.05, 0.115);
  for (const k of [-1, 1]) {
    const hol = M(new THREE.SphereGeometry(0.03, 6, 5), skinD, head);
    hol.position.set(k * 0.045, 0.04, 0.1);
    hol.scale.set(1.2, 0.6, 0.4);
  }
  glowSprite(head, '#9fb0ff', 0.18, V(0, -0.05, 0.13), 0.22);

  const skel: Record<string, THREE.Object3D> = { hips: hips.p, chest: chest.p, head: neck.p };
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    // Arms: down past the knees, the elbow low, long jointed fingers that nearly touch the ground.
    const sh = V(k * 0.19, 1.64, 0.08), el = V(k * 0.27, 1.12, 0.12), wr = V(k * 0.28, 0.62, 0.2);
    const arm = joint(chest.abs, sh, `arm${s}`);
    const fore = joint(arm.abs, el, `fore${s}`);
    limb(arm.abs, sh, el, 0.045, 0.035, skin, 6);
    M(new THREE.SphereGeometry(0.04, 6, 5), skinD, arm.abs).position.copy(el);
    limb(fore.abs, el, wr, 0.035, 0.028, skin, 6);
    for (let f = 0; f < 4; f++) {
      const a = (f - 1.5) * 0.18;
      const knuckle = V(wr.x + Math.sin(a) * 0.04 * k, wr.y - 0.18, wr.z + 0.04 + Math.cos(a) * 0.02);
      limb(fore.abs, V(wr.x, wr.y - 0.02, wr.z), knuckle, 0.012, 0.01, skin, 4);
      const tip = V(knuckle.x + Math.sin(a) * 0.03 * k, knuckle.y - 0.16, knuckle.z + 0.08);
      limb(fore.abs, knuckle, tip, 0.01, 0.005, skinD, 4);
      const n = M(new THREE.ConeGeometry(0.008, 0.04, 4), nail, fore.abs);
      n.position.copy(tip);
      n.rotation.x = Math.PI + 0.4;
    }
    // Legs: long and thin, knees bent a little, bare feet like the hands.
    const hip = V(k * 0.09, 0.98, -0.02), knee = V(k * 0.11, 0.52, 0.06), ank = V(k * 0.1, 0.08, -0.02);
    const leg = joint(hips.abs, hip, `leg${s}`);
    const shin = joint(leg.abs, knee, `shin${s}`);
    limb(leg.abs, hip, knee, 0.055, 0.04, skin, 6);
    M(new THREE.SphereGeometry(0.045, 6, 5), skinD, leg.abs).position.copy(knee);
    limb(shin.abs, knee, ank, 0.04, 0.03, skin, 6);
    const foot = M(new THREE.BoxGeometry(0.07, 0.035, 0.2), skinD, shin.abs);
    foot.position.set(ank.x, 0.03, ank.z + 0.07);
    Object.assign(skel, { [`arm${s}`]: arm.p, [`fore${s}`]: fore.p, [`leg${s}`]: leg.p, [`shin${s}`]: shin.p });
  }

  // Rags of the borrowed face and clothes: pale torn strips hanging off the shoulders, the chest and the head.
  const strips: { m: THREE.Mesh; base: number; ph: number }[] = [];
  const strip = (parent: THREE.Object3D, at: THREE.Vector3, len: number, w: number, tilt: number): void => {
    const geo = new THREE.PlaneGeometry(w, len, 1, 3);
    geo.translate(0, -len / 2, 0);
    const m = M(geo, rag, parent);
    m.position.copy(at);
    m.rotation.set(tilt, rng() * Math.PI, (rng() - 0.5) * 0.4);
    strips.push({ m, base: m.rotation.x, ph: rng() * 6 });
  };
  for (let i = 0; i < 5; i++) strip(chest.abs, V((rng() - 0.5) * 0.34, 1.62 - rng() * 0.15, 0.06 + rng() * 0.08), 0.22 + rng() * 0.2, 0.05 + rng() * 0.07, 0.15);
  for (let i = 0; i < 3; i++) strip(head, V((rng() - 0.5) * 0.18, -0.08, 0.06), 0.14 + rng() * 0.1, 0.05, 0.25);
  // A faint dark wisp at its feet: it is a nightmare all the same.
  const wisp = basic('#20232c', 0.3);
  const pool = M(new THREE.CircleGeometry(0.28, 12), wisp, g);
  pool.rotation.x = -Math.PI / 2;
  pool.position.y = 0.015;

  const tick = (n: number): void => {
    for (const s of strips) s.m.rotation.x = s.base + 0.18 * Math.sin(n * 0.9 + s.ph);
  };
  return done(c, 2.0, tick, undefined, bonesOf(c, skel));
};

/**
 * The Prestacaras in Cal's face (Caras prestadas 4, when every hero came and there is nobody absent to copy): a
 * young herder of the titan's tail, sheepskin vest, a crook-less empty hand, brown hair; the skin a shade too pale.
 * Built on the heroes' figure so it walks and strikes with their bones.
 */
export const calFace = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 4472);
  const skin = std('#cfc2b8', { roughness: 0.8 });
  const wool = std('#4a5048', { roughness: 1 });
  const f = figure({ legs: std('#3a342e', { roughness: 1 }), boots: std('#2a221c', { roughness: 0.9 }), torso: wool, arms: wool, hands: skin, skin });
  c.g.add(f.fig);
  const b = f.bodyAbs, h = f.headAbs;
  // The sheepskin vest of the herders, a rope belt.
  const vest = std('#b9b0a2', { roughness: 1, side: THREE.DoubleSide });
  M(new THREE.CylinderGeometry(0.18, 0.21, 0.42, 10, 1, true), vest, b).position.y = 1.18;
  M(new THREE.TorusGeometry(0.17, 0.018, 4, 12), std('#8a7458'), b).position.set(0, 0.95, 0);
  (b.children[b.children.length - 1] as THREE.Object3D).rotation.x = Math.PI / 2;
  // Brown hair; a face with nothing wrong in it but its stillness.
  const hair = std('#4a3426', { roughness: 1 });
  M(new THREE.SphereGeometry(0.128, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), hair, h).position.set(0, 1.65, -0.01);
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.013, 5, 4), std('#1a1c22'), h).position.set(k * 0.045, 1.655, 0.115);
  M(new THREE.BoxGeometry(0.045, 0.007, 0.01), std('#9a7a70'), h).position.set(0, 1.59, 0.116);
  return done(c, 1.8, () => undefined, f.rig);
};
